import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input, InputGroup, Spinner } from "@chakra-ui/react";
import { FiArrowLeft, FiCode, FiClock, FiSend, FiPlus } from "react-icons/fi";
import {
  rxAssistantService,
  type QueryResponse,
  type StreamEvent,
} from "../../services/rxAssistantService";
import { ResultRenderer } from "../../components/rxAssistant/ResultRenderer";
import { toaster } from "../../components/ui/toaster";

export const SessionDetail = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [sessionDetail, setSessionDetail] = useState<{
    id: number;
    title: string;
    created_at: string;
    updated_at: string;
    queries: QueryResponse[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [sessionDetail?.queries, isSending, streamingMessage]);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetail(parseInt(sessionId));
    }
  }, [sessionId]);

  const fetchSessionDetail = async (id: number) => {
    try {
      setIsLoading(true);
      const response = await rxAssistantService.getSessionById(id);
      setSessionDetail(response);
    } catch (error: any) {
      toaster.create({
        title: "Error",
        description:
          error.response?.data?.detail || "Failed to fetch session details",
        type: "error",
      });
      navigate("/ai-assistant/history");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!question.trim() || isSending || !sessionDetail) return;

    const queryText = question;
    setIsSending(true);
    setPendingQuestion(queryText);
    setQuestion("");
    setStreamingMessage("Thinking...");

    try {
      await rxAssistantService.sendQueryStream(
        {
          question: queryText,
          session_id: sessionDetail.id,
        },
        (event: StreamEvent) => {
          switch (event.type) {
            case "thinking":
              setStreamingMessage(event.message || "Thinking...");
              break;
            case "generating_sql":
              setStreamingMessage(event.message || "Generating SQL...");
              break;
            case "sql_generated":
              setStreamingMessage("Executing query...");
              break;
            case "executing":
              setStreamingMessage(event.message || "Executing...");
              break;
            case "complete":
              setStreamingMessage(null);
              if (event.query_id && event.session_id) {
                const newQuery: QueryResponse = {
                  query_id: event.query_id,
                  session_id: event.session_id,
                  question: event.question || queryText,
                  sql: event.sql || "",
                  visualization: event.visualization || {
                    type: "text",
                    title: "Result",
                    config: {},
                  },
                  results: event.results || [],
                  row_count: event.row_count || 0,
                  execution_time_ms: event.execution_time_ms || 0,
                  success: event.success !== false,
                  created_at: event.created_at || new Date().toISOString(),
                };
                setSessionDetail((prev) =>
                  prev
                    ? { ...prev, queries: [...prev.queries, newQuery] }
                    : prev,
                );
              }
              break;
            case "error":
              setStreamingMessage(null);
              toaster.create({
                title: "Error",
                description: event.message || "Failed to process query",
                type: "error",
                duration: 6000,
              });
              break;
          }
        },
      );
    } catch (error: any) {
      setStreamingMessage(null);
      const errorMessage = error.message || "Failed to send query";
      toaster.create({
        title: "Error",
        description: errorMessage,
        type: "error",
        duration: 5000,
      });
    } finally {
      setIsSending(false);
      setPendingQuestion(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "Just now";

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!sessionDetail) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Session not found</p>
      </div>
    );
  }

  return (
    <div className="h-[85dvh] w-full max-w-4xl mx-auto! px-4! flex flex-col">
      {/* Header */}
      <div className="shrink-0 sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100 py-4 mb-4 -mx-4 px-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/ai-assistant/history")}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
          aria-label="Back to history">
          <FiArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg! font-semibold! text-gray-900! truncate">
            {sessionDetail.title}
          </h1>
          <p className="text-xs! text-gray-500! flex items-center gap-1.5">
            <FiClock size={12} />
            {formatDate(sessionDetail.created_at)}
          </p>
        </div>
      </div>

      {/* Queries — scrollable */}
      <div className="flex-1 overflow-y-auto px-4">
        <div className="space-y-10 py-6">
          {sessionDetail.queries.length === 0 && !isSending ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              <p className="text-gray-500">
                No queries in this session yet. Ask a follow-up below!
              </p>
            </div>
          ) : (
            <>
              {sessionDetail.queries.map((query) => (
                <div key={query.query_id} className="space-y-6!">
                  {/* User Question */}
                  <div className="flex justify-end pl-20">
                    <div className="bg-[#E6ECF7] text-[#302E2E] rounded-md rounded-tr-none px-5! py-3! max-w-full md:max-w-2xl">
                      <p className="text-sm! leading-relaxed! font-medium! whitespace-pre-wrap">
                        {query.question}
                      </p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex justify-start pr-8">
                    <div className="w-full max-w-2xl space-y-3!">
                      {query.success ? (
                        <>
                          {/* Main Result */}
                          <div className="overflow-hidden">
                            <ResultRenderer
                              visualization={query.visualization}
                              results={query.results}
                              executionTime={query.execution_time_ms}
                            />
                          </div>

                          {/* SQL Query (Collapsible) */}
                          {query.sql && (
                            <div className="max-w-2xl">
                              <details className="group">
                                <summary className="list-none cursor-pointer inline-flex items-center gap-2 text-xs! font-medium! text-gray-500! hover:text-blue-600! transition-colors select-none py-1">
                                  <span className="bg-gray-100 group-open:bg-blue-50 p-1.5 rounded-md transition-colors">
                                    <FiCode size={14} />
                                  </span>
                                  <span>
                                    {query.sql.length > 50
                                      ? "View Generated SQL"
                                      : "View SQL"}
                                  </span>
                                </summary>
                                <div className="mt-2 ml-1 relative">
                                  <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                                  <div className="pl-6 py-1">
                                    <pre className="text-xs! overflow-x-auto whitespace-pre-wrap text-gray-600! font-mono! bg-gray-50! p-4! rounded-lg! border! border-gray-100!">
                                      {query.sql}
                                    </pre>
                                  </div>
                                </div>
                              </details>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="max-w-2xl bg-red-50 border border-red-100 rounded-xl p-4 flex gap-3 items-start">
                          <div className="mt-0.5 shrink-0 text-red-500">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="12"></line>
                              <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm! font-medium! text-red-800!">
                              Generation Failed
                            </h4>
                            <p className="text-sm! text-red-600! mt-1">
                              {query.error_message ||
                                "An error occurred while processing your request."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Streaming state for follow-up */}
              {isSending && pendingQuestion && (
                <div className="space-y-6">
                  <div className="flex justify-end pl-20">
                    <div className="bg-[#E6ECF7] text-[#302E2E] rounded-md rounded-tr-none px-5 py-3 max-w-full md:max-w-2xl opacity-70">
                      <p className="text-sm! leading-relaxed! font-medium! whitespace-pre-wrap">
                        {pendingQuestion}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-[#F5F5F5] px-5 py-4 rounded-md rounded-tl-none flex items-center gap-2">
                      <div className="flex space-x-1">
                        <div
                          className="w-2 h-2 bg-[#0142B1] rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}></div>
                        <div
                          className="w-2 h-2 bg-[#0142B1] rounded-full animate-bounce"
                          style={{ animationDelay: "150ms" }}></div>
                        <div
                          className="w-2 h-2 bg-[#0142B1] rounded-full animate-bounce"
                          style={{ animationDelay: "300ms" }}></div>
                      </div>
                      <span className="text-sm! font-medium! text-gray-500!">
                        {streamingMessage || "Analyzing..."}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Follow-up Input */}
      <div className="shrink-0 pt-3! pb-6!">
        <InputGroup
          startElement={<FiPlus />}
          endElement={
            <button
              onClick={() => handleFollowUp()}
              disabled={!question.trim() || isLoading}
              className="bg-[#0142B1]! h-6 w-6 flex items-center justify-center rounded-md text-white">
              <FiSend color="#fff" size={12} />
            </button>
          }>
          <Input
            size={"lg"}
            className="w-full"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about sales, revenue, trends... (e.g., 'revenue this month')"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleFollowUp();
              }
            }}
            disabled={isLoading}
          />
        </InputGroup>
        <p className="text-xs! text-gray-400! mt-2! text-center">
          Continue this conversation — the AI remembers prior questions in this
          session.
        </p>
      </div>
    </div>
  );
};
